import { FunnelIcon, TrashIcon, PlusIcon } from '@phosphor-icons/react';
import { useProjects } from '@/hooks/use-projects';
import { ReportsService } from '@/api';
import { useQuery } from '@tanstack/react-query';
import { FunnelReport } from '@/components/reports/funnel-report';
import { useCallback } from 'react';
import { DatePicker } from '@/components/date-picker';
import { createFileRoute, Link, useRouteContext } from '@tanstack/react-router';
import { reportsOptions } from '@/hooks/query-options';
import type { ReportConfig } from '@/types';
import { Button } from '@/components/ui/button';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { PageHeader } from '@/components/page-header';
import { PageContent } from '@/components/page-content';
import { toast } from 'sonner';
import { hasPermission } from '@/lib/permissions';

export const Route = createFileRoute('/_auth/_side-layout/funnels')({
  component: Funnels,
  beforeLoad: async ({ context }) => {
    const projectId = context.projectId;
    if (!projectId) {
      return;
    }
    context.queryClient.prefetchQuery(reportsOptions({ projectId }));
  },
});

type Report = {
  id: string;
  name: string;
  config: ReportConfig;
};

function Funnels() {
  const { projectId } = useProjects();
  const { userRole } = useRouteContext({ from: '/_auth' });
  const canEditProject = hasPermission(userRole, { project: ['update'] });
  const reportsQuery = useQuery({
    ...reportsOptions({ projectId: projectId! }),
    enabled: !!projectId,
  });

  const deleteReport = useCallback(
    (reportId: string) => {
      return ReportsService.deleteReport({ reportId })
        .then(() => reportsQuery.refetch())
        .then(() => toast.success('Report Deleted'))
        .catch(() =>
          toast.error('Failed to Delete Report', {
            description: 'Please try again.',
          }),
        );
    },
    [projectId],
  );

  // Only funnel reports belong on this page — journey reports share the same
  // reports table, so filter by config.type (otherwise a journey shows up here
  // as an empty heading with no chart).
  const funnelReports =
    reportsQuery.data?.filter(
      (report: Report) => report.config.type === 'funnel',
    ) ?? [];

  return (
    <>
      <PageHeader
        title="Funnels"
        description="Track conversion rates across multi-step user flows."
        actions={
          canEditProject ? (
            <Button asChild iconLeft={<PlusIcon />}>
              <Link to={'/funnels/add-funnel-report' as string}>
                Add Funnel Report
              </Link>
            </Button>
          ) : undefined
        }
      />
      <PageContent>
        {funnelReports.length === 0 ? (
          <Empty>
            <EmptyHeader>
              <EmptyMedia>
                <FunnelIcon className="size-12" />
              </EmptyMedia>
              <EmptyTitle>No Funnels Yet</EmptyTitle>
              <EmptyDescription>
                Create your first funnel to see how many users complete a series
                of steps.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <div className="flex flex-col gap-10">
            <div className="self-start">
              <DatePicker />
            </div>
            {funnelReports.map((report: Report) => (
              <div key={report.id} className="flex flex-col gap-3">
                <h2 className="flex items-center gap-2 text-xl font-semibold tracking-tight">
                  <span>{report.name}</span>
                  {canEditProject && (
                    <TrashIcon
                      className="cursor-pointer"
                      onClick={() => deleteReport(report.id)}
                    />
                  )}
                </h2>
                <div>
                  {report.config.type === 'funnel' && (
                    <FunnelReport config={report.config} />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </PageContent>
    </>
  );
}
